import {
  Button,
  Spinner,
  Text,
  Icon,
  useColorMode,
  useColorStyle,
  Flex,
} from '@tonic-one/react';
import {
  // PlanStatusIcon,
  SuccessIcon,
  WarningCircleIcon,
} from '@tonic-one/react-icons';
import React, { ReactNode, useEffect, useState } from 'react';
import {
  AppForSa,
  DeProvisionStatus,
  ServiceForReAuth,
} from '@Types/administrator';
import { useTranslation } from 'react-i18next';
import {
  AppData,
  MigrationStatus,
  SaModalNameEnum,
  SaModalState,
  Token,
} from './types';
import {
  TokenStatus,
  ProvisionStatus,
  ProvisionStatusEnum,
} from '@Types/provision';
import { PermissionNameEnum } from '@Types/permissions';
import { usePermissionStore } from '@Models/usePermissionModel';
import { ProvisionApi } from '@Apis';
import { useOrgStore } from '../../../models/useOrgModel';
import { useNavigateWithHandler } from '@Utils/hooks';
import { Service } from '@Types/app';

interface Data extends Token, AppData {}

const ValidIcon = ({ valid }: { valid: boolean }) => {
  const [colorMode] = useColorMode();
  const [colorStyle] = useColorStyle({ colorMode });
  const { t } = useTranslation('administration');

  if (valid) {
    return <Icon as={SuccessIcon} color={colorStyle.color.success} />;
  } else {
    return (
      <Flex align="center" gap="2x">
        <Icon as={WarningCircleIcon} color={colorStyle.color.error} />
        <Text>{t('administration.sa.table.status.tip.tokenInvalid')}</Text>
      </Flex>
    );
  }
};

const ExchangeOnlineStatus = ({
  orgId,
  data,
  setSaModalState,
}: {
  orgId: string;
  data: Data;
  setSaModalState: (v: SaModalState) => void;
}) => {
  const { widthEditablePermission } = usePermissionStore();
  const adminEditPermission = widthEditablePermission(
    PermissionNameEnum.administrator
  );
  const { t } = useTranslation('administration');
  if (data.isPoc) {
    return <ExchangeOnlinePocStatus {...{ orgId, data, setSaModalState }} />;
  }

  return (
    <Flex gap="2x" align="center" flexWrap="wrap">
      <ValidIcon valid={data.tokenStatus !== TokenStatus.Failure} />
      <Button
        disabled={!!data.reAuthDisabled || !adminEditPermission}
        variant="secondary"
        onClick={() => {
          setSaModalState({
            name: SaModalNameEnum.ReAuth,
            data: {
              orgId,
              service: ServiceForReAuth.Exchange,
              authAccount: data.email,
              provisionType: data.provisionType,
            },
          });
        }}
      >
        {t('administration.sa.table.status.btn.reAuth')}
      </Button>
    </Flex>
  );
};

const ExchangeOnlinePocStatus = ({
  orgId,
  data,
  setSaModalState,
}: {
  orgId: string;
  data: Data;
  setSaModalState: (v: SaModalState) => void;
}) => {
  const [colorMode] = useColorMode();
  const [colorStyle] = useColorStyle({ colorMode });
  const { switchOrgId } = useOrgStore();
  const navigate = useNavigateWithHandler();
  const { widthEditablePermission } = usePermissionStore();
  const adminEditPermission = widthEditablePermission(
    PermissionNameEnum.administrator
  );
  const { t } = useTranslation('administration');

  const [status, setStatus] = useState<ProvisionStatus>({
    status: ProvisionStatusEnum.Provisioned,
    isPocProvision: true,
  });

  useEffect(() => {
    ProvisionApi.apiGetProvisionStatus(Service.Exchange, orgId).then((res) => {
      setStatus(res.data);
    });
  }, [orgId]);

  if (status.status === ProvisionStatusEnum.TokenCreated) {
    return (
      <Flex gap="2x" align="center" flexWrap="wrap">
        <WarningCircleIcon color={colorStyle.color.warning} />
        {/* <PlanStatusIcon color={colorStyle.color.info} /> */}
        <Button
          disabled={!!data.reAuthDisabled || !adminEditPermission}
          variant="secondary"
          onClick={() => {
            setSaModalState({
              name: SaModalNameEnum.Provision,
              data: {
                orgId,
                app: AppForSa.Exchange,
              },
            });
          }}
        >
          {t('administration.sa.table.status.btn.pocContinue')}
        </Button>
      </Flex>
    );
  }

  return (
    <Flex gap="2x" align="center" flexWrap="wrap">
      <ValidIcon valid={data.tokenStatus !== TokenStatus.Failure} />
      <Button
        disabled={!!data.reAuthDisabled || !adminEditPermission}
        variant="secondary"
        onClick={() => {
          setSaModalState({
            name: SaModalNameEnum.ReAuth,
            data: {
              orgId,
              service: ServiceForReAuth.Exchange,
              authAccount: data.email,
              provisionType: data.provisionType,
            },
          });
        }}
      >
        {t('administration.sa.table.status.btn.reAuth')}
      </Button>
      <Button
        disabled={!!data.reAuthDisabled || !adminEditPermission}
        variant="secondary"
        onClick={() => {
          setSaModalState({
            name: SaModalNameEnum.PocExtend,
            data: {
              orgId,
            },
          });
        }}
      >
        {t('administration.sa.pocExtend.btn')}
      </Button>
      <Button
        disabled={!!data.reAuthDisabled || !adminEditPermission}
        variant="secondary"
        onClick={() => {
          switchOrgId(orgId);
          navigate('/policy/global-settings/synchronized-user-lists');
        }}
      >
        {t('administration.sa.table.status.btn.pocUserExtend')}
      </Button>
    </Flex>
  );
};

const ExchangeInlineStatus = ({
  orgId,
  data,
  setSaModalState,
}: {
  orgId: string;
  data: Data;
  setSaModalState: (v: SaModalState) => void;
}) => {
  const { widthEditablePermission } = usePermissionStore();
  const adminEditPermission = widthEditablePermission(
    PermissionNameEnum.administrator
  );
  const { t } = useTranslation('administration');

  return (
    <Flex gap="2x" align="center">
      <ValidIcon valid={data.tokenStatus !== TokenStatus.Failure} />
      <Button
        disabled={!!data.reAuthDisabled || !adminEditPermission}
        variant="secondary"
        onClick={() => {
          setSaModalState({
            name: SaModalNameEnum.ReAuth,
            data: {
              orgId,
              service: ServiceForReAuth.ExchangeInline,
              authAccount: data.email,
              provisionType: data.provisionType,
            },
          });
        }}
      >
        {t('administration.sa.table.status.btn.reAuth')}
      </Button>
    </Flex>
  );
};

const SharePointStatus = ({
  orgId,
  data,
  setSaModalState,
}: {
  orgId: string;
  data: Data;
  setSaModalState: (v: SaModalState) => void;
}) => {
  const { widthEditablePermission } = usePermissionStore();
  const adminEditPermission = widthEditablePermission(
    PermissionNameEnum.administrator
  );
  const { t } = useTranslation('administration');
  return (
    <Flex gap="2x" align="center">
      <ValidIcon valid={data.tokenStatus !== TokenStatus.Failure} />
      <Button
        disabled={!!data.reAuthDisabled || !adminEditPermission}
        variant="secondary"
        onClick={() => {
          setSaModalState({
            name: SaModalNameEnum.ReAuth,
            data: {
              orgId,
              service: ServiceForReAuth.SharePoint,
              authAccount: data.email,
              provisionType: data.provisionType,
            },
          });
        }}
        // disabled, HaveSomePendingTask
      >
        {t('administration.sa.table.status.btn.reAuth')}
      </Button>
    </Flex>
  );
  /////////////////////////////////////////////////////////////////  sp reauth
  /**
 *   $scope.BuildStatusForSPToken = function (row) {
    var text = '<img src="/images/icon_check.png">';
    if ($rootScope.SPTokenInvalidStatus[row.COMPANY_GUID]) {
      text = '<img src="/images/messageIcon_warning.png">';
      text =
        text + "<span ng-bind-html=\" 'HTML_PAGE.administration.office365_credentials.INVALID_TOKEN' | i18n\"></span>";
    }
    text =
      text +
      '&nbsp; [<button type="button" ng-disabled="HaveSomePendingTask(\'' +
      "GRAPH_SITE" +
      "','" +
      row.COMPANY_GUID +
      '\')" class="btn btn-link" style="padding-left:0px; padding-right:0px;" ng-click="cretae_sp_token(\'' +
      row.EMAIL +
      "','" +
      row.SERVICE_TYPE +
      "','" +
      row.COMPANY_GUID +
      "')\"  ng-bind-html=\" 'LABEL.CREATE_ACCESS_TOKEN' | i18n\"></button>]";
    return text;
  };
 */
};

const OneDriveStatus = ({
  orgId,
  data,
  setSaModalState,
}: {
  orgId: string;
  data: Data;
  setSaModalState: (v: SaModalState) => void;
}) => {
  const { widthEditablePermission } = usePermissionStore();
  const adminEditPermission = widthEditablePermission(
    PermissionNameEnum.administrator
  );
  const { t } = useTranslation('administration');

  return (
    <Flex gap="2x" align="center">
      <ValidIcon valid={data.tokenStatus !== TokenStatus.Failure} />
      <Button
        disabled={!!data.reAuthDisabled || !adminEditPermission}
        variant="secondary"
        onClick={() => {
          setSaModalState({
            name: SaModalNameEnum.ReAuth,
            data: {
              orgId,
              service: ServiceForReAuth.OneDrive,
              authAccount: data.email,
              provisionType: data.provisionType,
            },
          });
        }}
        // disabled, HaveSomePendingTask
      >
        {t('administration.sa.table.status.btn.reAuth')}
      </Button>
    </Flex>
  );
  /////////////////////////////////////////////////////////////////  od reauth
  /**
 *
  //has reauth
  $scope.BuildStatusForODToken = function (row) {
    var text = '<img src="/images/icon_check.png">';
    if ($rootScope.ODTokenInvalidStatus[row.COMPANY_GUID]) {
      text = '<img src="/images/messageIcon_warning.png">';
      text =
        text + "<span ng-bind-html=\" 'HTML_PAGE.administration.office365_credentials.INVALID_TOKEN' | i18n\"></span>";
    }
    text =
      text +
      '&nbsp; [<button type="button" ng-disabled="HaveSomePendingTask(\'' +
      "GRAPH_ONEDRIVE_PERSONAL_SITES_UPDATED" +
      "','" +
      row.COMPANY_GUID +
      '\')" class="btn btn-link" style="padding-left:0px; padding-right:0px;" ng-click="cretae_od_token(\'' +
      row.EMAIL +
      "','" +
      row.SERVICE_TYPE +
      "','" +
      row.COMPANY_GUID +
      "')\"  ng-bind-html=\" 'LABEL.CREATE_ACCESS_TOKEN' | i18n\"></button>]";
    return text;
  };
 */
};

const TeamsStatus = ({
  orgId,
  data,
  setSaModalState,
}: {
  orgId: string;
  data: Data;
  setSaModalState: (v: SaModalState) => void;
}) => {
  const { widthEditablePermission } = usePermissionStore();
  const adminEditPermission = widthEditablePermission(
    PermissionNameEnum.administrator
  );
  const { t } = useTranslation('administration');

  return (
    <Flex gap="2x" align="center">
      <ValidIcon valid={data.tokenStatus !== TokenStatus.Failure} />
      <Button
        disabled={!!data.reAuthDisabled || !adminEditPermission}
        variant="secondary"
        onClick={() => {
          setSaModalState({
            name: SaModalNameEnum.ReAuth,
            data: {
              orgId,
              service: ServiceForReAuth.Teams,
              authAccount: data.email,
              provisionType: data.provisionType,
            },
          });
        }}
        // disabled, HaveSomePendingTask GRAPH_EX_USERS_AND_GROUPS_UPDATED
      >
        {t('administration.sa.table.status.btn.reAuth')}
      </Button>
    </Flex>
  );
  ///////////////////////////////////////////////////////////////// teams reauth
  /*
 $scope.BuildStatusForTeamsToken = function (row) {
    var text = '<img src="/images/icon_check.png">';
    if ($rootScope.TeamsTokenInvalidStatus[row.COMPANY_GUID]) {
      text = '<img src="/images/messageIcon_warning.png">';
      text =
        text + "<span ng-bind-html=\" 'HTML_PAGE.administration.office365_credentials.INVALID_TOKEN' | i18n\"></span>";
    }
    text =
      text +
      '&nbsp; [<button type="button" ng-disabled="HaveSomePendingTask(\'' +
      "GRAPH_TEAMS_INFO_UPDATEm" +
      "','" +
      row.COMPANY_GUID +
      '\')" class="btn btn-link" style="padding-left:0px; padding-right:0px;" ng-click="cretae_teams_token(\'' +
      row.EMAIL +
      "','" +
      row.SERVICE_TYPE +
      "','" +
      row.COMPANY_GUID +
      "')\"  ng-bind-html=\" 'LABEL.CREATE_ACCESS_TOKEN' | i18n\"></button>]";
    return text;
  };
 */
};

const TeamsChatStatus = ({
  orgId,
  data,
  setSaModalState,
}: {
  orgId: string;
  data: Data;
  setSaModalState: (v: SaModalState) => void;
}) => {
  const { widthEditablePermission } = usePermissionStore();
  const adminEditPermission = widthEditablePermission(
    PermissionNameEnum.administrator
  );
  const [colorMode] = useColorMode();
  const [colorStyle] = useColorStyle({ colorMode });
  const { t } = useTranslation('administration');

  return (
    <Flex gap="2x" direction="column">
      <Flex gap="2x" align="center">
        <ValidIcon valid={data.tokenStatus !== TokenStatus.Failure} />
        <Button
          disabled={!!data.reAuthDisabled || !adminEditPermission}
          variant="secondary"
          onClick={() => {
            setSaModalState({
              name: SaModalNameEnum.ReAuth,
              data: {
                orgId,
                service: ServiceForReAuth.TeamsChat,
                authAccount: data.email,
                provisionType: data.provisionType,
              },
            });
          }}
          // disabled, HaveSomePendingTask GRAPH_EX_USERS_AND_GROUPS_UPDATED
        >
          {t('administration.sa.table.status.btn.reAuth')}
        </Button>
      </Flex>
      {/* <Divider variant="dashed" orientation="horizontal" /> */}
      <Flex gap="2x" align="center">
        {data.appOwner === 0 ? (
          <>
            <Icon as={WarningCircleIcon} color={colorStyle.color.warning} />
            <Button
              variant="secondary"
              onClick={() => {
                setSaModalState({
                  name: SaModalNameEnum.ProtectOwnApp,
                  data: {
                    orgId,
                  },
                });
              }}
              disabled={!adminEditPermission}
            >
              {t('administration.sa.table.status.btn.protectOwnApp')}
            </Button>
          </>
        ) : (
          <>
            {data.tokenStatus !== TokenStatus.Failure ? (
              <Icon as={SuccessIcon} color={colorStyle.color.success} />
            ) : (
              <Icon as={WarningCircleIcon} color={colorStyle.color.error} />
            )}
            <Button
              variant="secondary"
              onClick={() => {
                setSaModalState({
                  name: SaModalNameEnum.UpdateSecretOrChangeApp,
                  data: {
                    orgId,
                    appId: data.appId,
                  },
                });
              }}
              disabled={!adminEditPermission}
            >
              {t('administration.sa.table.status.btn.teamsChatUpdate')}
            </Button>
          </>
        )}
      </Flex>
    </Flex>
  );
  /////////////////////////////////////////////////////////////////teams chat
  /*
  $scope.BuildStatusForTeamsChatToken = function (row) {
    if (!$scope.teams_chat_token_status[row.COMPANY_GUID]) {
      var text = '<img src="/images/icon_x.png">';
    } else {
      var text = '<img src="/images/icon_check.png">';
    }
    if ($rootScope.TmChatTokenInvalidStatus[row.COMPANY_GUID]) {
      text = '<img src="/images/messageIcon_warning.png">';
      text =
        text + "<span ng-bind-html=\" 'HTML_PAGE.administration.office365_credentials.INVALID_TOKEN' | i18n\"></span>";
    }
    text =
      text +
      '&nbsp; [<button type="button" ng-disabled="HaveSomePendingTask(\'' +
      "GRAPH_TM_CHAT_USERS_AND_GROUPS_UPDATED" +
      "','" +
      row.COMPANY_GUID +
      '\')" class="btn btn-link" style="padding-left:0px; padding-right:0px;" ng-click="cretae_teams_chat_token(\'' +
      row.EMAIL +
      "','" +
      row.SERVICE_TYPE +
      "','" +
      row.COMPANY_GUID +
      "','" +
      show_teams_chat_owner(row.COMPANY_GUID) +
      "')\"  ng-bind-html=\" 'LABEL.CREATE_ACCESS_TOKEN' | i18n\"></button>]";
    var own_app_name;
    if (show_teams_chat_owner(row.COMPANY_GUID)) {
      // $rootScope.TmChatTokenInvalidStatus[row.COMPANY_GUID] ||
      if (!$scope.teams_chat_token_status[row.COMPANY_GUID]) {
        text =
          text +
          '<br> <div style="border-top: 1px solid #ccc;margin-top: 5px;padding-top: 5px;"><img src="/images/icon_x.png">' +
          "<span style='padding-left: 10px;' ng-bind-html=\" 'HTML_PAGE.administration.office365_credentials.TEAMS_CHAT_INVALID' | i18n\"></span>";
      } else {
        text =
          text +
          '<br> <div style="border-top: 1px solid #ccc;margin-top: 5px;padding-top: 5px;"><img src="/images/icon_check.png">';
      }
      own_app_name = "updata_secret";
      text =
        text +
        '&nbsp; [<button type="button" ng-disabled="HaveSomePendingTask(\'' +
        "GRAPH_TM_CHAT_USERS_AND_GROUPS_UPDATED" +
        "','" +
        row.COMPANY_GUID +
        '\')" class="btn btn-link" style="padding-left:0px; padding-right:0px;" ng-click="updata_teams_chat_app(\'' +
        row.EMAIL +
        "','" +
        row.SERVICE_TYPE +
        "','" +
        row.COMPANY_GUID +
        "','" +
        own_app_name +
        "')\" ng-bind-html=\" 'LABEL.UPDATA_OWN_APP' | i18n\"></button>]</div>";
    } else {
      own_app_name = "provision_app";
      text =
        text +
        '<br> <div style="border-top: 1px solid #ccc;margin-top: 5px;padding-top: 5px;"><img src="/images/icon_warning.png">' +
        '&nbsp; [<button type="button" ng-disabled="HaveSomePendingTask(\'' +
        "GRAPH_TM_CHAT_USERS_AND_GROUPS_UPDATED" +
        "','" +
        row.COMPANY_GUID +
        '\')" class="btn btn-link" style="padding-left:0px; padding-right:0px;" ng-click="updata_teams_chat_app(\'' +
        row.EMAIL +
        "','" +
        row.SERVICE_TYPE +
        "','" +
        row.COMPANY_GUID +
        "','" +
        own_app_name +
        "')\" ng-bind-html=\" 'LABEL.PROTECT_OWN_APP' | i18n\"></button>]</div>";
    }
    return text;
  };
*/
};

const GmailStatus = ({
  orgId,
  data,
  setSaModalState,
}: {
  orgId: string;
  data: Data;
  setSaModalState: (v: SaModalState) => void;
}) => {
  const { widthEditablePermission } = usePermissionStore();
  const adminEditPermission = widthEditablePermission(
    PermissionNameEnum.administrator
  );
  const { t } = useTranslation('administration');
  return (
    <Flex gap="2x" align="center">
      <ValidIcon valid={data.tokenStatus !== TokenStatus.Failure} />

      <Button
        disabled={!!data.reAuthDisabled || !adminEditPermission}
        variant="secondary"
        onClick={() => {
          setSaModalState({
            name: SaModalNameEnum.ReAuth,
            data: {
              orgId,
              service: ServiceForReAuth.Gmail,
              authAccount: data.email,
              provisionType: data.provisionType,
            },
          });
        }}
      >
        {t('administration.sa.table.status.btn.reAuth')}
      </Button>
    </Flex>
  );
  /////////////////////////////////////////////////////////////////  gmail reauth
  /*
  $scope.BuildStatusForGmailToken = function (row) {
    var text = '<img src="/images/icon_check.png">';
    //检查是否是无效token
    if ($rootScope.GmailTokenInvalidStatus[row.COMPANY_GUID]) {
      text = '<img src="/images/messageIcon_warning.png">';
      text =
        text + "<span ng-bind-html=\" 'HTML_PAGE.administration.office365_credentials.INVALID_TOKEN' | i18n\"></span>";
    }
    text =
      text +
      '&nbsp; [<button type="button" class="btn btn-link" style="padding-left:0px;' +
      ' padding-right:0px;" ng-click="create_gmail_token(\'' +
      row.COMPANY_GUID +
      "')\"  ng-bind-html=\" 'LABEL.CREATE_ACCESS_TOKEN' | i18n\"></button>]";
    return text;
  };
*/
};

const GmailInlineStatus = ({
  orgId,
  data,
  setSaModalState,
}: {
  orgId: string;
  data: Data;
  setSaModalState: (v: SaModalState) => void;
}) => {
  const { widthEditablePermission } = usePermissionStore();
  const adminEditPermission = widthEditablePermission(
    PermissionNameEnum.administrator
  );
  const { t } = useTranslation('administration');

  return (
    <Flex gap="2x" align="center">
      <ValidIcon valid={data.tokenStatus !== TokenStatus.Failure} />

      <Button
        disabled={!!data.reAuthDisabled || !adminEditPermission}
        variant="secondary"
        onClick={() => {
          setSaModalState({
            name: SaModalNameEnum.ReAuth,
            data: {
              orgId,
              service: ServiceForReAuth.GmailInline,
              authAccount: data.email,
              provisionType: data.provisionType,
            },
          });
        }}
      >
        {t('administration.sa.table.status.btn.reAuth')}
      </Button>
    </Flex>
  );
  /////////////////////////////////////////////////////////////////  gmail inline reauth
  /*
  $scope.BuildStatusForGmailInlineToken = function (row) {
    var text = '<img src="/images/icon_check.png">';
    if ($rootScope.GmailInlineTokenInvalidStatus[row.COMPANY_GUID]) {
      text = '<img src="/images/messageIcon_warning.png">';
      text =
        text + "<span ng-bind-html=\" 'HTML_PAGE.administration.office365_credentials.INVALID_TOKEN' | i18n\"></span>";
    }
    text =
      text +
      '&nbsp; [<button type="button" ng-disabled="HaveSomePendingTask(\'' +
      "GMAIL_INLINE_USERS_AND_GROUPS_UPDATED" +
      "','" +
      row.COMPANY_GUID +
      '\')" class="btn btn-link" style="padding-left:0px; padding-right:0px;" ng-click="cretae_gmail_inline_token(\'' +
      row.EMAIL +
      "','" +
      row.SERVICE_TYPE +
      "','" +
      row.COMPANY_GUID +
      "')\"  ng-bind-html=\" 'LABEL.CREATE_ACCESS_TOKEN' | i18n\"></button>]";
    if ($rootScope.gmail_inline_migration && $rootScope.gmail_inline_migration[row.COMPANY_GUID]) {
      text =
        text +
        '<br> <div style="border-top: 1px solid #ccc;margin-top: 5px;padding-top: 5px;"><img style="width:24px;" src="/images/icon_inline_migration.png">' +
        '&nbsp; [<button type="button" ng-disabled="HaveSomePendingTask(\'' +
        "GMAIL_INLINE_USERS_AND_GROUPS_UPDATED" +
        "','" +
        row.COMPANY_GUID +
        '\')" class="btn btn-link" style="padding-left:0px; padding-right:0px;" ng-click="gmail_inline_for_migration(\'' +
        row.EMAIL +
        "','" +
        row.SERVICE_TYPE +
        "','" +
        row.COMPANY_GUID +
        "')\" ng-bind-html=\" 'HTML_PAGE.administration.office365_credentials.UPGRADE_GMAIL_OUTBOUND_PROTECTION' | i18n\"></button>]</div>";
    }
    return text;
  };
*/
};

const BoxStatus = ({
  orgId,
  data,
  setSaModalState,
}: {
  orgId: string;
  data: Data;
  setSaModalState: (v: SaModalState) => void;
}) => {
  const { widthEditablePermission } = usePermissionStore();
  const adminEditPermission = widthEditablePermission(
    PermissionNameEnum.administrator
  );
  const { t } = useTranslation('administration');

  return (
    <Flex gap="2x" align="center">
      <ValidIcon valid={data.tokenStatus !== TokenStatus.Failure} />

      <Button
        disabled={!!data.reAuthDisabled || !adminEditPermission}
        variant="secondary"
        onClick={() => {
          setSaModalState({
            name: SaModalNameEnum.ReAuth,
            data: {
              orgId,
              service: ServiceForReAuth.Box,
              authAccount: data.email,
              provisionType: data.provisionType,
            },
          });
        }}
      >
        {t('administration.sa.table.status.btn.reAuth')}
      </Button>
    </Flex>
  );
  /////////////////////////////////////////////////////////////////  box reauth
  /*
  $scope.BuildStatusForBoxToken = function (row) {
    var text = '<img src="/images/icon_check.png">';
    if ($rootScope.BoxTokenInvalidStatus[row.COMPANY_GUID]) {
      text = '<img src="/images/messageIcon_warning.png">';
      text =
        text + "<span ng-bind-html=\" 'HTML_PAGE.administration.office365_credentials.INVALID_TOKEN' | i18n\"></span>";
    }
    text =
      text +
      '&nbsp; [<button type="button" ng-disabled="HaveSomePendingTask(\'' +
      "BOX_ACCOUNT_AUTO_PROVISION" +
      "','" +
      row.COMPANY_GUID +
      '\')" class="btn btn-link" style="padding-left:0px; padding-right:0px;" ng-click="cretae_box_token(\'' +
      row.COMPANY_GUID +
      "')\"  ng-bind-html=\" 'LABEL.CREATE_ACCESS_TOKEN' | i18n\"></button>]";
    return text;
  };
*/
};

const MIPStatus = ({
  orgId,
  data,
  setSaModalState,
}: {
  orgId: string;
  data: Data;
  setSaModalState: (v: SaModalState) => void;
}) => {
  const { widthEditablePermission } = usePermissionStore();
  const adminEditPermission = widthEditablePermission(
    PermissionNameEnum.administrator
  );
  const { t } = useTranslation('administration');

  return (
    <Flex gap="2x" align="center">
      <ValidIcon
        valid={
          data.tokenStatus !== TokenStatus.Failure &&
          // previous situation, mip without rms plan, just has 43,45,46 token
          data.tokenTypes.length === 4
        }
      />

      <Button
        disabled={!!data.reAuthDisabled || !adminEditPermission}
        variant="secondary"
        onClick={() => {
          setSaModalState({
            name: SaModalNameEnum.ReAuth,
            data: {
              orgId,
              service: ServiceForReAuth.Mip,
              authAccount: data.email,
              provisionType: data.provisionType,
            },
          });
        }}
      >
        {t('administration.sa.table.status.btn.reAuth')}
      </Button>
    </Flex>
  );
  /////////////////////////////////////////////////////////////////   MIP reauth
  /*
  $scope.BuildStatusForMIPToken = function (row) {
    var text = '<img src="/images/icon_check.png">';
    text =
      text +
      '&nbsp; [<button type="button" ng-disabled="HaveSomePendingTask(\'' +
      "GRAPH_MIP_LABELS_INFO_UPDATED" +
      "','" +
      row.COMPANY_GUID +
      '\')" class="btn btn-link" style="padding-left:0px; padding-right:0px;" ng-click="cretae_mip_token(\'' +
      row.EMAIL +
      "','" +
      row.SERVICE_TYPE +
      "','" +
      row.COMPANY_GUID +
      "')\"  ng-bind-html=\" 'LABEL.CREATE_ACCESS_TOKEN' | i18n\"></button>]";
    return text;
  };
*/
};

const IdentityProtectionStatus = ({
  // orgId,
  data,
  // setSaModalState,
}: {
  orgId: string;
  data: Data;
  setSaModalState: (v: SaModalState) => void;
}) => {
  // const { widthEditablePermission } = usePermissionStore();
  // const adminEditPermission = widthEditablePermission(
  //   PermissionNameEnum.administrator
  // );
  // const { t } = useTranslation('administration');

  return (
    <Flex gap="2x" align="center">
      <ValidIcon valid={data.tokenStatus !== TokenStatus.Failure} />
      {/*<Button*/}
      {/*  disabled={!!data.reAuthDisabled || !adminEditPermission}*/}
      {/*  variant="secondary"*/}
      {/*  onClick={() => {*/}
      {/*    setSaModalState({*/}
      {/*      name: SaModalNameEnum.ReAuth,*/}
      {/*      data: {*/}
      {/*        orgId,*/}
      {/*        service: ServiceForReAuth.Teams,*/}
      {/*        authAccount: data.email,*/}
      {/*      },*/}
      {/*    });*/}
      {/*  }}*/}
      {/*  // disabled, HaveSomePendingTask GRAPH_EX_USERS_AND_GROUPS_UPDATED*/}
      {/*>*/}
      {/*  {t('administration.sa.table.status.btn.reAuth')}*/}
      {/*</Button>*/}
    </Flex>
  );
};

const ExchangeServerStatus = ({
  orgId,
  data,
  setSaModalState,
}: {
  orgId: string;
  data: Data;
  setSaModalState: (v: SaModalState) => void;
}) => {
  const { widthEditablePermission } = usePermissionStore();
  const adminEditPermission = widthEditablePermission(
    PermissionNameEnum.administrator
  );
  const { t } = useTranslation('administration');
  const onClick = () => {
    setSaModalState({
      name: SaModalNameEnum.Extend,
      data: {
        orgId,
      },
    });
  };
  return (
    <Flex gap="2x" align="center">
      <Text>
        {t('administration.sa.table.status.tip.exchangeServerRegistered', {
          extra: data.extra,
        })}
      </Text>
      <Button
        variant="secondary"
        onClick={onClick}
        disabled={!adminEditPermission}
      >
        {t('administration.sa.table.status.btn.extendExpiration')}
      </Button>
    </Flex>
  );
};

const DropboxStatus = ({
  orgId,
  data,
  setSaModalState,
}: {
  orgId: string;
  data: Data;
  setSaModalState: (v: SaModalState) => void;
}) => {
  const { widthEditablePermission } = usePermissionStore();
  const adminEditPermission = widthEditablePermission(
    PermissionNameEnum.administrator
  );
  const { t } = useTranslation('administration');

  return (
    <Flex gap="2x" align="center">
      <ValidIcon valid={data.tokenStatus !== TokenStatus.Failure} />
      <Button
        disabled={!!data.reAuthDisabled || !adminEditPermission}
        variant="secondary"
        onClick={() => {
          setSaModalState({
            name: SaModalNameEnum.ReAuth,
            data: {
              orgId,
              service: ServiceForReAuth.Dropbox,
              authAccount: data.email,
              provisionType: data.provisionType,
            },
          });
        }}
      >
        {t('administration.sa.table.status.btn.reAuth')}
      </Button>
    </Flex>
  );
};

const GoogleDriveStatus = ({
  orgId,
  data,
  setSaModalState,
}: {
  orgId: string;
  data: Data;
  setSaModalState: (v: SaModalState) => void;
}) => {
  const { widthEditablePermission } = usePermissionStore();
  const adminEditPermission = widthEditablePermission(
    PermissionNameEnum.administrator
  );
  const { t } = useTranslation('administration');
  return (
    <Flex gap="2x" align="center">
      <ValidIcon valid={data.tokenStatus !== TokenStatus.Failure} />
      <Button
        disabled={!!data.reAuthDisabled || !adminEditPermission}
        variant="secondary"
        onClick={() => {
          setSaModalState({
            name: SaModalNameEnum.ReAuth,
            data: {
              orgId,
              service: ServiceForReAuth.GoogleDrive,
              authAccount: data.email,
              provisionType: data.provisionType,
            },
          });
        }}
      >
        {t('administration.sa.table.status.btn.reAuth')}
      </Button>
    </Flex>
  );
};

const DeprecatedSpodStatue = ({
  orgId,
  data,
  setSaModalState,
}: {
  orgId: string;
  data: Data;
  setSaModalState: (v: SaModalState) => void;
}) => {
  const { widthEditablePermission } = usePermissionStore();
  const adminEditPermission = widthEditablePermission(
    PermissionNameEnum.administrator
  );
  const [colorMode] = useColorMode();
  const [colorStyle] = useColorStyle({ colorMode });
  const { t } = useTranslation('administration');
  const [migrationStatus, setMigrationStatus] = useState<MigrationStatus>(
    MigrationStatus.Migrated
  );

  const onChangePwd = () => {
    setSaModalState({
      name: SaModalNameEnum.ChangePassword,
      data: {
        orgId,
        email: data.email,
      },
    });
  };

  let MigrationBtn: ReactNode;
  switch (migrationStatus) {
    case MigrationStatus.Failure:
    case MigrationStatus.NotStarted:
    case MigrationStatus.CreateToken:
      MigrationBtn = (
        <Button
          variant="secondary"
          onClick={() => {
            setSaModalState({
              name: SaModalNameEnum.SpodMigration,
              data: {
                orgId,
              },
            });
          }}
          disabled={!adminEditPermission}
        >
          {t('administration.sa.table.status.btn.spodMigrate')}
        </Button>
      );
      break;
    case MigrationStatus.Cleanup:
      MigrationBtn = (
        <Button
          variant="secondary"
          onClick={() => {
            setSaModalState({
              name: SaModalNameEnum.CleanUp,
              data: {
                orgId,
              },
            });
          }}
          disabled={!adminEditPermission}
        >
          {t('administration.sa.table.status.btn.delegatorAccountCleanup')}
        </Button>
      );
      break;
    case MigrationStatus.Migrating:
      MigrationBtn = (
        <Text>
          {t('administration.sa.table.status.btn.migrationCleanupInProgress')}
        </Text>
      );
      break;
    default:
      MigrationBtn = <></>;
  }

  useEffect(() => {
    (async () => {
      ProvisionApi.apiGetSpodMigrationStatus(orgId)
        .then((res) => {
          setMigrationStatus(res.data.status);
        })
        .catch(() => {
          console.error('apiGetSpodMigrationStatus error');
        });
    })();
  }, [orgId]);

  return (
    <Flex gap="2x" align="center">
      {data.migrationStatus === 2 ? (
        <Icon as={WarningCircleIcon} color={colorStyle.color.warning} />
      ) : (
        <Icon as={SuccessIcon} color={colorStyle.color.success} />
      )}
      {MigrationBtn}
      {data.provisionFromGraphapi !== 1 && (
        <Button
          variant="secondary"
          onClick={onChangePwd}
          disabled={!adminEditPermission}
        >
          {t('administration.sa.table.status.btn.changePwd')}
        </Button>
      )}
    </Flex>
  );
};

const RMSStatus = () => {
  return <></>;
};

const SfProductionStatus = () => {
  return <></>;
};

const SfSandboxStatus = () => {
  return <></>;
};

const SATableStatusMap: {
  [key in AppForSa]: React.FC<{
    orgId: string;
    data: Data;
    setSaModalState: (v: SaModalState) => void;
  }>;
} = {
  [AppForSa.Exchange]: ExchangeOnlineStatus,
  [AppForSa.ExchangeInline]: ExchangeInlineStatus,
  [AppForSa.SharePoint]: SharePointStatus,
  [AppForSa.OneDrive]: OneDriveStatus,
  [AppForSa.Teams]: TeamsStatus,
  [AppForSa.TeamsChat]: TeamsChatStatus,
  [AppForSa.Gmail]: GmailStatus,
  [AppForSa.GmailInline]: GmailInlineStatus,
  [AppForSa.Box]: BoxStatus,
  [AppForSa.Mip]: MIPStatus,
  [AppForSa.IdentityProtection]: IdentityProtectionStatus,
  [AppForSa.ExchangeServer]: ExchangeServerStatus,
  [AppForSa.Dropbox]: DropboxStatus,
  [AppForSa.GoogleDrive]: GoogleDriveStatus,
  [AppForSa.DeprecatedSpod]: DeprecatedSpodStatue,
  [AppForSa.RMS]: RMSStatus,
  [AppForSa.SalesforceProduction]: SfProductionStatus,
  [AppForSa.SalesforceSandbox]: SfSandboxStatus,
};

const SATableStatus = ({
  orgId,
  data,
  setSaModalState,
}: {
  orgId: string;
  data: Data;
  setSaModalState: (v: SaModalState) => void;
}) => {
  const AppStatus = SATableStatusMap[data.app] as
    | React.FC<{
        setSaModalState: (v: SaModalState) => void;
        orgId: string;
        data: any;
      }>
    | undefined;

  if (!AppStatus) return <></>;

  if (
    data.deprovisioningStatus === DeProvisionStatus.DeProvisioning ||
    data.deprovisioningStatus === DeProvisionStatus.Failure
  ) {
    return (
      <DeprovisionStatus
        status={data.deprovisioningStatus}
        orgId={orgId}
        app={data.app}
        setSaModalState={setSaModalState}
      />
    );
  }
  return (
    <AppStatus orgId={orgId} setSaModalState={setSaModalState} data={data} />
  );
};

export default SATableStatus;

/////////////////////////////////////////////////////////////////  ex inline
/*

  // $scope.BuildStatusForExInlineToken = function (row) {
  //   var text = '<img src="/images/icon_check.png">';
  //   if ($rootScope.ExInlineTokenInvalidStatus[row.COMPANY_GUID]) {
  //     text = '<img src="/images/messageIcon_warning.png">';
  //     text =
  //       text + "<span ng-bind-html=\" 'HTML_PAGE.administration.office365_credentials.INVALID_TOKEN' | i18n\"></span>";
  //   }
  //   text =
  //     text +
  //     '&nbsp; [<button type="button" ng-disabled="HaveSomePendingTask(\'' +
  //     "GRAPH_EX_INLINE" +
  //     "','" +
  //     row.COMPANY_GUID +
  //     '\')" class="btn btn-link" style="padding-left:0px; padding-right:0px;" ng-click="cretae_inline_token(\'' +
  //     row.EMAIL +
  //     "','" +
  //     row.SERVICE_TYPE +
  //     "','" +
  //     row.COMPANY_GUID +
  //     "')\"  ng-bind-html=\" 'LABEL.CREATE_ACCESS_TOKEN' | i18n\"></button>]";
  //   if ($rootScope.old_inline_outbound && $rootScope.old_inline_outbound[row.COMPANY_GUID]) {
  //     if ($rootScope.old_inline_outbound[row.COMPANY_GUID]) {
  //       if ($rootScope.taskStatusPendingProvisions("MIGRATION_FOR_EXCHANGE_INLINE_STEPS", row.COMPANY_GUID)) {
  //         text =
  //           text +
  //           '<br> <div style="border-top: 1px solid #ccc;margin-top: 5px;padding-top: 5px;"><img style="width:24px;" src="/images/loading.gif">' +
  //           "&nbsp; <span style='font-family: 'Arial';color: #222222;' ng-bind-html=\" 'HTML_PAGE.administration.office365_credentials.ADDING_OUTBOUND_PROTECTION' | i18n\"></span>";
  //       } else {
  //         text =
  //           text +
  //           '<br> <div style="border-top: 1px solid #ccc;margin-top: 5px;padding-top: 5px;"><img style="width:24px;" src="/images/icon_inline_migration.png">' +
  //           '&nbsp; [<button type="button" ng-disabled="HaveSomePendingTask(\'' +
  //           "GRAPH_EX_INLINE" +
  //           "','" +
  //           row.COMPANY_GUID +
  //           '\')" class="btn btn-link" style="padding-left:0px; padding-right:0px;" ng-click="add_outbound_protection(\'' +
  //           row.EMAIL +
  //           "','" +
  //           row.SERVICE_TYPE +
  //           "','" +
  //           row.COMPANY_GUID +
  //           "')\" ng-bind-html=\" 'LABEL.ADD_OUTBOUND_PROTECTION' | i18n\"></button>]</div>";
  //       }
  //     }
  //   }

  //   return text;
  // };
*/

///////////////////////////////////////////////////////////////// RMS 不做
/*
  $scope.BuildStatusForRMS = function (row) {
    var text = '<img src="/images/icon_check.png">';
    if (
      $scope.set_service_status(
        {
          id: 46,
        },
        row.COMPANY_GUID
      )
    ) {
      text =
        '<img src="/images/icon_warning.png"  ps-popover data-content="' +
        $filter("i18n")("HTML_PAGE.administration.office365_credentials.REMOVE_RMS_ACCOUNT_TIP") +
        '">';
      text =
        text +
        '&nbsp; [<button type="button" class="btn btn-link" style="padding-left:0px;' +
        ' padding-right:0px;" ng-click="click_remove_rms_token(\'' +
        row.COMPANY_GUID +
        "')\"  ng-bind-html=\" 'LABEL.REMOVE_RMS_ACCOUNT' | i18n\"></button>]";
    } else {
      text =
        '<img src="/images/icon_warning.png"  ps-popover data-content="' +
        $filter("i18n")("HTML_PAGE.administration.office365_credentials.UPGRADE_RMS_ACCOUNT_TIP") +
        '">';
      text =
        text +
        '&nbsp; [<button type="button" class="btn btn-link" style="padding-left:0px;' +
        ' padding-right:0px;" ng-click="click_upgrade_rms_to_mip(\'' +
        row.COMPANY_GUID +
        "')\"  ng-bind-html=\" 'LABEL.UPGRADE_RMS_TO_MIP' | i18n\"></button>]";
    }
    return text;
  };
*/

///////////////////////////////////////////////////////////////////SalesforceProduction 有 但是不做
/*
    $scope.BuildStatusForSalesforce ProductionToken = function (row) {
    var text = '<img src="/images/icon_check.png">';
    if ($rootScope.SalesforceProductionTokenInvalidStatus[row.COMPANY_GUID]) {
      text = '<img src="/images/messageIcon_warning.png">';
      text =
        text + "<span ng-bind-html=\" 'HTML_PAGE.administration.office365_credentials.INVALID_TOKEN' | i18n\"></span>";
    }
    text =
      text +
      '&nbsp; [<button type="button" ng-disabled="HaveSomePendingTask(\'' +
      "BOX_ACCOUNT_AUTO_PROVISION" +
      "','" +
      row.COMPANY_GUID +
      '\')" class="btn btn-link" style="padding-left:0px; padding-right:0px;" ng-click="cretae_salesforce_production_token(\'' +
      row.EMAIL +
      "','" +
      row.COMPANY_GUID +
      "')\"  ng-bind-html=\" 'LABEL.CREATE_ACCESS_TOKEN' | i18n\"></button>]";
    return text;
  };
  */

/////////////////////////////////////////////////////////////////// SalesforceSandboxT  有但是不做
/*
    $scope.BuildStatusForSalesforceSandboxToken = function (row) {
    var text = '<img src="/images/icon_check.png">';
    if ($rootScope.SalesforceSandboxTokenInvalidStatus[row.COMPANY_GUID]) {
      text = '<img src="/images/messageIcon_warning.png">';
      text =
        text + "<span ng-bind-html=\" 'HTML_PAGE.administration.office365_credentials.INVALID_TOKEN' | i18n\"></span>";
    }
    text =
      text +
      '&nbsp; [<button type="button" ng-disabled="HaveSomePendingTask(\'' +
      "BOX_ACCOUNT_AUTO_PROVISION" +
      "','" +
      row.COMPANY_GUID +
      '\')" class="btn btn-link" style="padding-left:0px; padding-right:0px;" ng-click="cretae_salesforce_sandbox_token(\'' +
      row.EMAIL +
      "','" +
      row.COMPANY_GUID +
      "')\"  ng-bind-html=\" 'LABEL.CREATE_ACCESS_TOKEN' | i18n\"></button>]";
    return text;
  };
*/

const DeprovisionStatus = ({
  status,
  orgId,
  app,
  setSaModalState,
}: {
  status: DeProvisionStatus.Failure | DeProvisionStatus.DeProvisioning;
  orgId: string;
  app: AppForSa;
  setSaModalState: (v: SaModalState) => void;
}) => {
  const { t } = useTranslation('administration');
  const { widthEditablePermission } = usePermissionStore();
  const onClick = () => {
    setSaModalState({
      name: SaModalNameEnum.Details,
      data: {
        orgId,
        app,
        status,
      },
    });
  };

  return (
    <Flex gap="2x" align="center">
      {status === DeProvisionStatus.Failure ? (
        <Text>{t('administration.sa.table.status.tip.deprovisionFail')}</Text>
      ) : (
        <>
          <Spinner size="sm" />
          <Text>{t('administration.sa.table.status.tip.deprovisioning')}</Text>
        </>
      )}
      <Button
        variant="secondary"
        onClick={onClick}
        disabled={!widthEditablePermission(PermissionNameEnum.administrator)}
      >
        {t('administration.sa.table.status.btn.details')}
      </Button>
    </Flex>
  );
};
